import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { graphTrpc } from '@org/clients/graph';
import {
  Skeleton,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Input,
} from '@org/ui';
import { Search, Trophy, MessageCircle } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/members')({
  component: MembersPage,
});

function MembersPage() {
  const { t } = useTranslation('community');
  const [search, setSearch] = useState('');

  const { data: leaderboard, isLoading } = graphTrpc.points.getLeaderboard.useQuery({
    type: 'all-time',
    limit: 50,
  });

  const members = leaderboard?.leaderboard ?? [];
  const filteredMembers = members.filter((member) => {
    const name = member.displayName ||
      [member.firstName, member.lastName].filter(Boolean).join(' ') ||
      '';
    return name.toLowerCase().includes(search.toLowerCase());
  });

  const getLevelFromPoints = (points: number) => {
    if (points >= 10000) return { name: 'Diamond', color: 'text-cyan-500' };
    if (points >= 5000) return { name: 'Platinum', color: 'text-purple-500' };
    if (points >= 2000) return { name: 'Gold', color: 'text-yellow-500' };
    if (points >= 500) return { name: 'Silver', color: 'text-gray-400' };
    return { name: 'Bronze', color: 'text-amber-700' };
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('members.title')}</h1>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('members.searchPlaceholder')}
          aria-label={t('members.searchPlaceholder')}
          className="pl-10"
        />
      </div>

      {/* Members Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-center gap-3">
                <Skeleton className="h-12 w-12 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-3 w-16" />
                </div>
              </div>
            </div>
          ))
        ) : filteredMembers.length === 0 ? (
          <div className="col-span-full rounded-2xl border border-border bg-card p-8 text-center">
            <p className="text-muted-foreground">
              {search ? t('members.noResults') : t('members.empty')}
            </p>
          </div>
        ) : (
          filteredMembers.map((member, index) => {
            const name = member.displayName ||
              [member.firstName, member.lastName].filter(Boolean).join(' ') ||
              'Anonymous';
            const initial = name.slice(0, 1).toUpperCase();
            const level = getLevelFromPoints(member.points);

            return (
              <div
                key={member.userId}
                className="rounded-2xl border border-border bg-card p-4 transition-shadow hover:shadow-md"
              >
                <div className="flex items-start gap-3">
                  <Link to={`/profile/${member.userId}`}>
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={member.profileImageUrl ?? undefined} />
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {initial}
                      </AvatarFallback>
                    </Avatar>
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/profile/${member.userId}`} className="font-medium hover:underline">
                      {name}
                    </Link>
                    <div className="mt-1 flex items-center gap-2">
                      <Trophy className={`h-3 w-3 ${level.color}`} aria-hidden="true" />
                      <span className={`text-xs ${level.color}`}>{level.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {member.points.toLocaleString()} pts
                      </span>
                    </div>
                    {index < 3 && (
                      <div className="mt-1">
                        <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                          {t('members.topContributor', { rank: index + 1 })}
                        </span>
                      </div>
                    )}
                  </div>
                  <Link to={`/messages/${member.userId}`}>
                    <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={t('profile.sendMessage')}>
                      <MessageCircle className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </Link>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
